/**
 * OrgMultiSelect — searchable multi-select for the 125 banks.
 *
 * v1 trade-offs:
 *   - Plain HTML, no library — keeps the bundle small.
 *   - Closes on outside click; keyboard nav (↑/↓/Enter) deferred to v2.
 *   - "All banks" = empty selection (passes no orgIds filter to the API).
 *
 * Caller owns the selected-ids array and the bank list.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

import Ico from './Ico';
import type { Bank } from '../type/api';

interface Props {
	banks: Bank[];
	selected: number[];
	onChange: (next: number[]) => void;
	placeholder?: string;
	maxChipsShown?: number;
}

export default function OrgMultiSelect({
	banks,
	selected,
	onChange,
	placeholder = 'All banks',
	maxChipsShown = 3,
}: Props) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const ref = useRef<HTMLDivElement | null>(null);

	const safeBanks = Array.isArray(banks) ? banks : [];
	const selectedSet = useMemo(() => new Set(selected), [selected]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return safeBanks;
		return safeBanks.filter(
			(b) => String(b.orgId).includes(q) || (b.orgName || '').toLowerCase().includes(q),
		);
	}, [safeBanks, query]);

	useEffect(() => {
		if (!open) return;
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		};
		window.addEventListener('mousedown', handler);
		return () => window.removeEventListener('mousedown', handler);
	}, [open]);

	const toggle = (orgId: number) => {
		const next = new Set(selected);
		if (next.has(orgId)) next.delete(orgId);
		else next.add(orgId);
		onChange(Array.from(next).sort((a, b) => a - b));
	};

	const clearAll = () => onChange([]);

	const selectedBanks = useMemo(
		() => safeBanks.filter((b) => selectedSet.has(b.orgId)),
		[safeBanks, selectedSet],
	);

	const summary =
		selected.length === 0
			? placeholder
			: selected.length <= maxChipsShown
			? selectedBanks.map((b) => b.orgName || `#${b.orgId}`).join(', ')
			: `${selected.length} banks selected`;

	return (
		<div className="org-ms" ref={ref} style={{ position: 'relative' }}>
			<button
				type="button"
				className="org-ms-trigger"
				onClick={() => setOpen((o) => !o)}
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<Ico name="filter" size={12} />
				<span className="org-ms-summary">{summary}</span>
				{selected.length > 0 && (
					<span
						className="org-ms-clear"
						role="button"
						tabIndex={0}
						aria-label="Clear bank filter"
						onClick={(e) => {
							e.stopPropagation();
							clearAll();
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								e.stopPropagation();
								clearAll();
							}
						}}
					>
						×
					</span>
				)}
				<Ico name="chevron-down" size={12} />
			</button>

			{open && (
				<div className="org-ms-pop" role="listbox">
					<div className="org-ms-search">
						<Ico name="search" size={12} />
						<input
							autoFocus
							type="search"
							placeholder="Search by name or ID…"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
						/>
					</div>
					<div className="org-ms-actions">
						<button type="button" onClick={clearAll}>
							Clear
						</button>
						<span style={{ color: 'var(--text-faint)', fontSize: 11 }}>
							{selected.length} of {safeBanks.length} selected
						</span>
					</div>
					<div className="org-ms-list">
						{filtered.length === 0 && (
							<div className="org-ms-empty">No banks match.</div>
						)}
						{filtered.map((b) => {
							const checked = selectedSet.has(b.orgId);
							return (
								<label key={b.orgId} className={'org-ms-row' + (checked ? ' checked' : '')}>
									<input
										type="checkbox"
										checked={checked}
										onChange={() => toggle(b.orgId)}
									/>
									<span className="org-ms-id">{b.orgId}</span>
									<span className="org-ms-name">{b.orgName || '—'}</span>
								</label>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
